import { useEffect, useRef } from "react";
import { expectsTrustedOrigins, getUserEventSource } from "@/utils/utils.ts";
import useSegmentExportedTrackCreated from "@/common/hooks/event-source/user/useSegmentExportedTrackCreated.tsx";
import usePointConversationUserMentionedShowSnackbar
  from "@/common/hooks/event-source/user/usePointConversationUserMentionedShowSnackbar.tsx";
import usePointConversationUserMentionedShowNotification
  from "@/common/hooks/event-source/user/usePointConversationUserMentionedShowNotification.tsx";
import usePointConversationUpdated from "@/common/hooks/event-source/user/usePointConversationUpdated.tsx";
import useSegmentConversationUserMentionedShowSnackbar
  from "@/common/hooks/event-source/user/useSegmentConversationUserMentionedShowSnackbar.tsx";
import useSegmentConversationUserMentionedShowNotification
  from "@/common/hooks/event-source/user/useSegmentConversationUserMentionedShowNotification.tsx";
import useSegmentConversationUpdated from "@/common/hooks/event-source/user/useSegmentConversationUpdated.tsx";
import useTracksUploadFinished from "@/common/hooks/event-source/user/useTracksUploadFinished.tsx";
import useTrackNameChanged from "@/common/hooks/event-source/user/useTrackNameChanged.tsx";
import useTrackDuplicated from "@/common/hooks/event-source/user/useTrackDuplicated.tsx";
import useTrackSourceChanged from "@/common/hooks/event-source/user/useTrackSourceChanged.tsx";
import useWaveformCreated from "@/common/hooks/event-source/user/useWaveformCreated.tsx";
import useMoveExplorerItem from "@/common/hooks/event-source/user/useMoveExplorerItem.tsx";
import useUserPlaybackSynced from "@/common/hooks/event-source/user/useUserPlaybackSynced.tsx";
import usePlaylistCreated from "@/common/hooks/event-source/user/usePlaylistCreated.tsx";
import usePlaylistDeleted from "@/common/hooks/event-source/user/usePlaylistDeleted.tsx";
import useTrackPlaylistChanged from "@/common/hooks/event-source/user/useTrackPlaylistChanged.tsx";
import useTracksPlaylistChanged from "@/common/hooks/event-source/user/useTracksPlaylistChanged.tsx";
import useTrackDuplicatedToOtherPlaylist from "./user/useTrackDuplicatedToOtherPlaylist";
import useTrackDeleted from "@/common/hooks/event-source/user/useTrackDeleted.tsx";
import useTracksDeleted from "@/common/hooks/event-source/user/useTracksDeleted.tsx";
import useTrackInfoChanged from "@/common/hooks/event-source/user/useTrackInfoChanged.tsx";
import useSegmentCreated from "@/common/hooks/event-source/user/useSegmentCreated.tsx";
import useSegmentUpdate from "@/common/hooks/event-source/user/useSegmentUpdate.tsx";
import useSegmentDelete from "@/common/hooks/event-source/user/useSegmentDelete.tsx";
import useSegmentAudioCrop from "@/common/hooks/event-source/user/useSegmentAudioCrop.tsx";
import usePointCreate from "@/common/hooks/event-source/user/usePointCreate.tsx";
import usePointUpdate from "@/common/hooks/event-source/user/usePointUpdate.tsx";
import usePointDelete from "@/common/hooks/event-source/user/usePointDelete.tsx";
import useGeneratePeaksProgress from "@/common/hooks/event-source/user/useGeneratePeaksProgress.tsx";
import useExportTrackProgress from "@/common/hooks/event-source/user/useExportTrackProgress.tsx";
import useDownloadTrack from "@/common/hooks/event-source/user/useDownloadTrack.tsx";
import useDownloadTracks from "@/common/hooks/event-source/user/useDownloadTracks.tsx";
import useDownloadTracksState from "@/common/hooks/event-source/user/useDownloadTracksState.tsx";
import useUserEventPlayingTrack from "./user/useUserEventPlayingTrack";
import useUserEventSharedTrack from "./user/useUserEventSharedTrack";
import useUserEventSharedSegment from "@/common/hooks/event-source/user/useUserEventSharedSegment.tsx";
import useSessionUser from "@/common/hooks/useSessionUser.ts";
import usePointConversationItemDeleted from "@/common/hooks/event-source/user/usePointConversationItemDeleted.tsx";
import useSegmentConversationItemDeleted from "@/common/hooks/event-source/user/useSegmentConversationItemDeleted.tsx";
import useSharedTrackShowNotification from "@/common/hooks/event-source/user/useSharedTrackShowNotification.tsx";
import usePlaylistParentChanged from "@/common/hooks/event-source/user/usePlaylistParentChanged.tsx";

export const addUserEventSourceListener = (
  eventSource: EventSource,
  messageType: string,
  eventFunction: (e: MessageEvent) => void,
) => {
  const listener = (e: MessageEvent) => {
    expectsTrustedOrigins(e);
    eventFunction(e);
  };

  eventSource.addEventListener(messageType, listener);
  return () => {
    eventSource.removeEventListener(messageType, listener);
  };
};

export const useUserApiEventSource = () => {
  const user = useSessionUser();
  const eventSourceRef = useRef<EventSource | null>(null);
  const isInitialized = useRef(false);

  useEffect(() => {
    if (!user || isInitialized.current) {
      return;
    }

    const eventSource = getUserEventSource(user);
    eventSourceRef.current = eventSource;
    isInitialized.current = true;

    return () => {
      /*console.log('Closing EventSource');
      eventSource.close();
      eventSourceRef.current = null;
      isInitialized.current = false;*/
    };
  }, [user]);

  useSegmentExportedTrackCreated(eventSourceRef)
  // useUploadFinishedTrackCreated(eventSourceRef)
  usePointConversationUserMentionedShowSnackbar(eventSourceRef)
  useSharedTrackShowNotification(eventSourceRef)
  usePointConversationUserMentionedShowNotification(eventSourceRef)
  usePointConversationUpdated(eventSourceRef)
  usePointConversationItemDeleted(eventSourceRef)
  useSegmentConversationItemDeleted(eventSourceRef)
  useSegmentConversationUserMentionedShowSnackbar(eventSourceRef)
  useSegmentConversationUserMentionedShowNotification(eventSourceRef)
  useSegmentConversationUpdated(eventSourceRef)
  useTracksUploadFinished(eventSourceRef)
  useTrackNameChanged(eventSourceRef)
  useTrackDuplicated(eventSourceRef)
  useTrackSourceChanged(eventSourceRef)
  useWaveformCreated(eventSourceRef)
  useMoveExplorerItem(eventSourceRef)
  useUserPlaybackSynced(eventSourceRef)
  usePlaylistCreated(eventSourceRef)
  usePlaylistDeleted(eventSourceRef)
  useTrackPlaylistChanged(eventSourceRef)
  useTracksPlaylistChanged(eventSourceRef)
  usePlaylistParentChanged(eventSourceRef)
  useTrackDuplicatedToOtherPlaylist(eventSourceRef)
  useTrackDeleted(eventSourceRef)
  useTracksDeleted(eventSourceRef)
  useTrackInfoChanged(eventSourceRef)
  useSegmentCreated(eventSourceRef)
  useSegmentUpdate(eventSourceRef)
  useSegmentDelete(eventSourceRef)
  useSegmentAudioCrop(eventSourceRef)
  usePointCreate(eventSourceRef)
  usePointUpdate(eventSourceRef)
  usePointDelete(eventSourceRef)
  useGeneratePeaksProgress(eventSourceRef)
  useExportTrackProgress(eventSourceRef)
  useDownloadTrack(eventSourceRef)
  useDownloadTracks(eventSourceRef)
  useDownloadTracksState(eventSourceRef)
  useUserEventPlayingTrack(eventSourceRef)
  useUserEventSharedTrack(eventSourceRef)
  useUserEventSharedSegment(eventSourceRef)

  return null;
}
